import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFirestoreInstance } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

export interface ErrorLog {
  message: string;
  stack?: string;
  type: 'api' | 'runtime' | 'unhandled' | 'boundary' | 'promise' | '404' | 'oops' | 'slow_request' | 'critical' | 'auth';
  url?: string;
  endpoint?: string;
  statusCode?: number;
  userId?: string;
  userEmail?: string;
  platform: 'web' | 'ios-speech' | 'ios-interview' | 'ios-ielts';
  userAgent?: string;
  additionalInfo?: Record<string, unknown>;
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

// Queue for errors that occur before Firebase is initialized
let errorQueue: ErrorLog[] = [];
let isInitialized = false;

// Get current user info from localStorage or auth state
const getUserInfo = (): { userId?: string; userEmail?: string } => {
  try {
    // Try to get from localStorage (set during login)
    const userId = localStorage.getItem('litrlly_user_id');
    const userEmail = localStorage.getItem('litrlly_user_email');
    return { userId: userId || undefined, userEmail: userEmail || undefined };
  } catch {
    return {};
  }
};

// Send error notification to backend (triggers Telegram notification)
const sendTelegramNotification = async (errorLog: ErrorLog): Promise<void> => {
  try {
    await fetch(`${API_BASE_URL}/api/errors/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(errorLog),
    });
    console.log('[ErrorLogger] Telegram notification sent');
  } catch (notifyError) {
    // Don't fail silently but don't break the app either
    console.error('[ErrorLogger] Failed to send Telegram notification:', notifyError);
  }
};

// Log error to Firestore
export const logError = async (error: Partial<ErrorLog>): Promise<void> => {
  const errorLog: ErrorLog = {
    message: error.message || 'Unknown error',
    stack: error.stack,
    type: error.type || 'runtime',
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    endpoint: error.endpoint,
    statusCode: error.statusCode,
    platform: 'web',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    additionalInfo: error.additionalInfo,
    ...getUserInfo(),
  };

  // Send Telegram notification (don't await to avoid blocking)
  sendTelegramNotification(errorLog);

  // If Firebase not initialized, queue the error
  if (!isInitialized) {
    errorQueue.push(errorLog);
    console.error('[ErrorLogger] Queued error (Firebase not ready):', errorLog.message);
    return;
  }

  try {
    const db = getFirestoreInstance();
    await addDoc(collection(db, 'errors'), {
      ...errorLog,
      timestamp: serverTimestamp(),
    });
    console.error('[ErrorLogger] Logged to Firestore:', errorLog.message);
  } catch (firestoreError) {
    // Fallback to console if Firestore fails
    console.error('[ErrorLogger] Failed to log to Firestore:', firestoreError);
    console.error('[ErrorLogger] Original error:', errorLog);
  }
};

// Process queued errors after Firebase initializes
export const initializeErrorLogger = async (): Promise<void> => {
  isInitialized = true;

  // Process queued errors
  if (errorQueue.length > 0) {
    console.log(`[ErrorLogger] Processing ${errorQueue.length} queued errors`);
    for (const error of errorQueue) {
      await logError(error);
    }
    errorQueue = [];
  }
};

// Log API errors
export const logApiError = (
  endpoint: string,
  error: Error | unknown,
  statusCode?: number,
  requestData?: unknown
): void => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : undefined;

  logError({
    message: `API Error: ${endpoint} - ${errorMessage}`,
    stack: errorStack,
    type: 'api',
    endpoint,
    statusCode,
    additionalInfo: {
      requestData: requestData ? JSON.stringify(requestData).slice(0, 500) : undefined,
    },
  });
};

// Log runtime errors
export const logRuntimeError = (error: Error, componentStack?: string): void => {
  logError({
    message: error.message,
    stack: error.stack,
    type: 'runtime',
    additionalInfo: componentStack ? { componentStack } : undefined,
  });
};

// Log unhandled errors
export const logUnhandledError = (
  message: string,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
): void => {
  logError({
    message,
    stack: error?.stack,
    type: 'unhandled',
    additionalInfo: {
      source,
      lineno,
      colno,
    },
  });
};

// Log unhandled promise rejections
export const logPromiseRejection = (reason: unknown): void => {
  const message = reason instanceof Error ? reason.message : String(reason);
  const stack = reason instanceof Error ? reason.stack : undefined;

  logError({
    message: `Unhandled Promise Rejection: ${message}`,
    stack,
    type: 'promise',
  });
};

// Log React Error Boundary errors
export const logBoundaryError = (error: Error, errorInfo: { componentStack: string }): void => {
  logError({
    message: `React Error Boundary: ${error.message}`,
    stack: error.stack,
    type: 'boundary',
    additionalInfo: {
      componentStack: errorInfo.componentStack,
    },
  });
};

// Set user info for error tracking (call after login)
export const setErrorLoggerUser = (userId: string, userEmail?: string): void => {
  try {
    localStorage.setItem('litrlly_user_id', userId);
    if (userEmail) {
      localStorage.setItem('litrlly_user_email', userEmail);
    }
  } catch {
    // Ignore localStorage errors
  }
};

// Clear user info (call after logout)
export const clearErrorLoggerUser = (): void => {
  try {
    localStorage.removeItem('litrlly_user_id');
    localStorage.removeItem('litrlly_user_email');
  } catch {
    // Ignore localStorage errors
  }
};

// Log 404 page view
export const log404Error = (attemptedPath: string): void => {
  logError({
    message: `404 Page Not Found: ${attemptedPath}`,
    type: '404',
    additionalInfo: {
      attemptedPath,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    },
  });
};

// Log oops/error page view (when ErrorBoundary renders fallback)
export const logOopsError = (error?: Error, componentStack?: string): void => {
  logError({
    message: `Oops Page Shown: ${error?.message || 'Unknown error'}`,
    stack: error?.stack,
    type: 'oops',
    additionalInfo: componentStack ? { componentStack } : undefined,
  });
};

// Log slow request warning (when request takes more than threshold)
export const logSlowRequest = (
  endpoint: string,
  method: string,
  waitTimeSeconds: number
): void => {
  logError({
    message: `Slow Request: ${method} ${endpoint} - User waiting ${waitTimeSeconds}s+`,
    type: 'slow_request',
    endpoint,
    additionalInfo: {
      method,
      waitTimeSeconds,
      pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      pageTitle: typeof document !== 'undefined' ? document.title : undefined,
    },
  });
};

// Log critical authentication errors
export const logAuthError = (
  errorCode: string,
  errorMessage: string,
  attemptedEmail?: string,
  additionalContext?: Record<string, unknown>
): void => {
  logError({
    message: `CRITICAL Auth Error: ${errorCode} - ${errorMessage}`,
    type: 'auth',
    severity: 'critical',
    additionalInfo: {
      errorCode,
      attemptedEmail: attemptedEmail ? `${attemptedEmail.substring(0, 3)}***` : undefined,
      timestamp: new Date().toISOString(),
      networkInfo: {
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        connectionType: typeof navigator !== 'undefined' && 'connection' in navigator
          ? (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType
          : undefined,
      },
      ...additionalContext,
    },
  });
};
